/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineNewspaper, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Action, Column } from "../../ui";
import { useBlogContext } from "../../context/BlogContext";
import { pushOneBlog } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type Blog = {
  blog_id: number | string;
  title?: string;
  description?: string;
  thumbnail?: string;
  createdAt?: string;
  [key: string]: unknown;
};

const addRoute = "/blog/add";
const editRoute = "/blog/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { blogs, count, fetchBlogs, deleteBlog, updateBlogState } =
    useBlogContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!blogs) fetchBlogs(page, pageSize);
  }, [blogs]);

  const rows = useMemo<Blog[] | null>(() => {
    if (!Array.isArray(blogs)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return blogs as Blog[];
    return (blogs as Blog[]).filter((b) =>
      String(b.title ?? "").toLowerCase().includes(q),
    );
  }, [blogs, query]);

  const columns: Column<Blog>[] = [
    {
      header: "Title",
      cell: (row) => (
        <div className="flex items-center gap-3">
          {row.thumbnail ? (
            <img
              src={row.thumbnail}
              alt={row.title ?? ""}
              className="h-10 w-16 rounded-md object-cover ring-1 ring-gray-200"
            />
          ) : (
            <span className="flex h-10 w-16 items-center justify-center rounded-md bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
              <HiOutlineNewspaper className="h-4 w-4" />
            </span>
          )}
          <div className="min-w-0">
            <p className="truncate font-medium text-gray-900">
              {row.title ?? "—"}
            </p>
            {row.createdAt ? (
              <p className="text-[11.5px] text-gray-500">
                {new Date(row.createdAt).toDateString()}
              </p>
            ) : null}
          </div>
        </div>
      ),
    },
  ];

  const handleDelete = async (row: Blog) => {
    const response: any = await deleteBlog(row.blog_id);
    if (response?.status === 200 && Array.isArray(blogs)) {
      updateBlogState(
        (blogs as Blog[]).filter((b) => b.blog_id !== row.blog_id),
      );
    }
  };

  const rowActions = (row: Blog): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneBlog(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.title ?? "this blog post"}?`,
        description: "Visitors will no longer be able to read this post.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<Blog>
      title="Blog"
      subtitle="Editorial posts published on the public site."
      icon={<HiOutlineNewspaper className="h-5 w-5" />}
      actions={[{ label: "Add post", variant: "primary", href: addRoute }]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.blog_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search blog posts…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchBlogs(p, ps);
        },
      }}
      empty="No blog posts yet."
    />
  );
};

export default Overview;
